import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PlusCircle, Globe, AlertTriangle, Loader2, ArrowUpRight, Zap } from "lucide-react";
import api from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { scoreColor, scoreLabel } from "@/components/ScoreGauge";
import { Button } from "@/components/ui/button";

function Sparkline({ history, color }) {
  const scores = (history || []).map((h) => h.score);
  if (scores.length < 2) return <div className="h-8 text-xs text-muted-foreground">No trend yet</div>;
  const lo = Math.min(...scores);
  const span = Math.max(1, Math.max(...scores) - lo);
  const points = scores
    .map((s, i) => `${(i / (scores.length - 1)) * 100},${28 - ((s - lo) / span) * 24}`)
    .join(" ");
  return (
    <svg className="h-8 w-full" viewBox="0 0 100 32" preserveAspectRatio="none" aria-hidden="true">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "reachrep", label: "ReachRep" },
  { key: "manual", label: "Manual" },
];

export default function Dashboard() {
  const [sites, setSites] = useState(null);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();
  const poll = useRef(null);

  const load = () => api.get("/sites").then((r) => setSites(r.data)).catch(() => setSites([]));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const anyAuditing = Array.isArray(sites) && sites.some((s) => s.status === "auditing");
    clearInterval(poll.current);
    if (anyAuditing) poll.current = setInterval(load, 4000);
    return () => clearInterval(poll.current);
  }, [sites]);

  const pipeline = (sites || []).filter((s) => s.source === "reachrep");
  const gateScores = pipeline.filter((s) => s.gate_score != null).map((s) => s.gate_score);
  const avgGate = gateScores.length ? Math.round(gateScores.reduce((a, b) => a + b, 0) / gateScores.length) : null;
  const shown = (sites || []).filter((s) => filter === "all" || (s.source || "manual") === filter);

  return (
    <AppLayout>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Portfolio</h1>
          <p className="mt-1 text-sm text-muted-foreground">Every managed site, scored across search, local and AI visibility.</p>
        </div>
        <Button data-testid="dashboard-new-audit" onClick={() => navigate("/new")}>
          <PlusCircle className="mr-2 h-4 w-4" /> New Audit
        </Button>
      </div>

      {pipeline.length > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-6 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-5 py-4" data-testid="enroll-stats">
          <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
            <Zap className="h-5 w-5" /><span className="text-sm font-semibold uppercase tracking-widest">ReachRep Enrollments</span>
          </div>
          <div><span className="font-mono text-2xl font-medium">{pipeline.length}</span><span className="ml-1 text-xs text-muted-foreground">sites enrolled</span></div>
          {avgGate != null && <div><span className="font-mono text-2xl font-medium" style={{ color: scoreColor(avgGate) }}>{avgGate}</span><span className="ml-1 text-xs text-muted-foreground">avg gate score</span></div>}
        </div>
      )}

      {sites && sites.length > 0 && (
        <div className="mb-5 flex gap-2" data-testid="source-filters">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`source-filter-${f.key}`}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${filter === f.key ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {f.label}{f.key === "reachrep" ? ` (${pipeline.length})` : ""}
            </button>
          ))}
        </div>
      )}

      {sites === null && (
        <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading portfolio…</div>
      )}

      {sites && sites.length === 0 && (
        <div data-testid="empty-portfolio" className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-24 text-center">
          <Globe className="mb-4 h-10 w-10 text-muted-foreground" />
          <h3 className="font-display text-xl font-semibold">Enter a website and go</h3>
          <p className="mt-2 max-w-sm text-sm text-muted-foreground">Add your first site. Beacon crawls it, scores it, and hands you a prioritized fix list in under a minute.</p>
          <Button data-testid="empty-new-audit" className="mt-6" onClick={() => navigate("/new")}><PlusCircle className="mr-2 h-4 w-4" /> Run your first audit</Button>
        </div>
      )}

      {sites && sites.length > 0 && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((s, idx) => {
            const color = scoreColor(s.latest_score);
            let host = s.url;
            try { host = new URL(s.url).hostname.replace("www.", ""); } catch {}
            const auditing = s.status === "auditing";
            const errored = s.status === "error";
            return (
              <motion.div key={s.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: idx * 0.04 }}>
                <Link to={`/site/${s.id}`} data-testid="site-card" className="group block rounded-lg border border-border bg-card p-5 transition-colors hover:border-primary/50">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-medium">{s.business_name || host}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">{s.platform || (auditing ? "Analyzing…" : "—")}</p>
                    </div>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>

                  {(s.source === "reachrep" || (s.gate_score != null)) && (
                    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="site-source-badges">
                      {s.source === "reachrep" && <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest text-emerald-600 dark:text-emerald-400">ReachRep</span>}
                      {s.gate_score != null && <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">Gate {s.gate_score}</span>}
                    </div>
                  )}

                  <div className="mt-4 flex items-end justify-between">
                    {auditing ? (
                      <div className="flex items-center gap-2 text-sm text-primary" data-testid="site-status-auditing"><Loader2 className="h-4 w-4 animate-spin" /> Auditing…</div>
                    ) : errored ? (
                      <div className="text-sm text-destructive">Unreachable</div>
                    ) : (
                      <div>
                        <span className="font-mono text-4xl font-medium tracking-tight" style={{ color }}>{s.latest_score ?? "—"}</span>
                        <span className="ml-1 text-xs text-muted-foreground">/100</span>
                        <p className="mt-1 text-xs uppercase tracking-widest" style={{ color }}>{scoreLabel(s.latest_score)}</p>
                      </div>
                    )}
                    {!auditing && !errored && <div className="w-24"><Sparkline history={s.history} color={color} /></div>}
                  </div>

                  <div className="mt-4 flex items-center gap-2 border-t border-border pt-3 text-xs text-muted-foreground">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                    {s.latest_issue_count ?? 0} open issues
                    {s.monitoring_frequency && s.monitoring_frequency !== "none" && (
                      <span className="ml-auto rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-emerald-600 dark:text-emerald-400">{s.monitoring_frequency}</span>
                    )}
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
